/**
 * Role Configuration — ADD-IC-002 Decision 3.1
 *
 * Defines the IC role taxonomy and per-role navigation.
 * Each role sees only the sections relevant to their function.
 */

import type { ICRole } from '@/types';

export interface NavItem {
  label: string;
  path: string;
  icon: string; // Lucide icon name
}

/**
 * Per-role navigation structure.
 * Order defines sidebar rendering order.
 */
export const ROLE_NAVIGATION: Record<ICRole, NavItem[]> = {
  fund_manager: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Funds', path: '/funds', icon: 'landmark' },
    { label: 'Investments', path: '/investments', icon: 'briefcase' },
    { label: 'Investees', path: '/investees', icon: 'building-2' },
    { label: 'Knowledge Graph', path: '/knowledge-graph', icon: 'network' },
    { label: 'Geographic', path: '/geographic', icon: 'globe' },
    { label: 'BI Studio', path: '/bi', icon: 'bar-chart-3' },
    { label: 'AI Assistant', path: '/ai', icon: 'sparkles' },
    { label: 'ESG Review', path: '/esg', icon: 'leaf' },
    { label: 'Compliance', path: '/compliance', icon: 'shield-check' },
    { label: 'Regulatory Monitor', path: '/regulatory', icon: 'shield-alert' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
    { label: 'Reports', path: '/reports', icon: 'file-text' },
    { label: 'Settings', path: '/settings', icon: 'settings' },
  ],

  org_admin: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Funds', path: '/funds', icon: 'landmark' },
    { label: 'Investments', path: '/investments', icon: 'briefcase' },
    { label: 'Investees', path: '/investees', icon: 'building-2' },
    { label: 'Knowledge Graph', path: '/knowledge-graph', icon: 'network' },
    { label: 'Geographic', path: '/geographic', icon: 'globe' },
    { label: 'BI Studio', path: '/bi', icon: 'bar-chart-3' },
    { label: 'AI Assistant', path: '/ai', icon: 'sparkles' },
    { label: 'ESG Review', path: '/esg', icon: 'leaf' },
    { label: 'CSRD', path: '/csrd', icon: 'book-open' },
    { label: 'Compliance', path: '/compliance', icon: 'shield-check' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
    { label: 'Reports', path: '/reports', icon: 'file-text' },
    { label: 'Users', path: '/users', icon: 'users' },
    { label: 'Settings', path: '/settings', icon: 'settings' },
  ],

  compliance_officer: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'SFDR', path: '/sfdr', icon: 'shield' },
    { label: 'PAI Indicators', path: '/pai', icon: 'bar-chart' },
    { label: 'EU Taxonomy', path: '/taxonomy', icon: 'git-branch' },
    { label: 'CSRD', path: '/csrd', icon: 'book-open' },
    { label: 'Regulatory Monitor', path: '/regulatory', icon: 'shield-alert' },
    { label: 'Filings', path: '/filings', icon: 'file-check' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
    { label: 'Calendar', path: '/calendar', icon: 'calendar' },
  ],

  investee_reporter: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Submit Data', path: '/submit', icon: 'clipboard-check' },
    { label: 'My Submissions', path: '/submissions', icon: 'file-text' },
    { label: 'Benchmarks', path: '/benchmarks', icon: 'bar-chart-2' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
  ],

  lp_viewer: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Portfolio', path: '/portfolio', icon: 'pie-chart' },
    { label: 'Fund Performance', path: '/performance', icon: 'trending-up' },
    { label: 'Reports', path: '/reports', icon: 'file-text' },
    { label: 'ESG Summary', path: '/esg-summary', icon: 'leaf' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
  ],

  investment_officer: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Pipeline', path: '/pipeline', icon: 'git-pull-request' },
    { label: 'Investments', path: '/investments', icon: 'briefcase' },
    { label: 'Due Diligence', path: '/due-diligence', icon: 'search' },
    { label: 'Investees', path: '/investees', icon: 'building-2' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
  ],

  impact_officer: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'ESG Data', path: '/esg', icon: 'leaf' },
    { label: 'Impact Metrics', path: '/impact', icon: 'target' },
    { label: 'SDG Mapping', path: '/sdg', icon: 'globe' },
    { label: 'Benchmarks', path: '/benchmarks', icon: 'bar-chart-2' },
    { label: 'Tasks', path: '/tasks', icon: 'check-square' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
  ],

  auditor: [
    { label: 'Dashboard', path: '/', icon: 'layout-dashboard' },
    { label: 'Audit Trail', path: '/audit', icon: 'eye' },
    { label: 'Data Snapshots', path: '/snapshots', icon: 'camera' },
    { label: 'Messages', path: '/messages', icon: 'mail' },
  ],
};

/**
 * Human-readable labels for each IC role.
 */
export const ROLE_LABELS: Record<ICRole, string> = {
  org_admin: 'Organization Admin',
  fund_manager: 'Fund Manager',
  investment_officer: 'Investment Officer',
  impact_officer: 'Impact Officer',
  compliance_officer: 'Compliance Officer',
  investee_reporter: 'Investee Reporter',
  lp_viewer: 'LP Viewer',
  auditor: 'Auditor',
};

/**
 * Map SI Platform roles to the closest IC role.
 * Falls back to 'fund_manager' if no mapping exists.
 */
export function mapSIRoleToICRole(siRole: string): ICRole {
  const mapping: Record<string, ICRole> = {
    admin: 'org_admin',
    super_admin: 'org_admin',
    fund_manager: 'fund_manager',
    investment_officer: 'investment_officer',
    impact_officer: 'impact_officer',
    compliance_officer: 'compliance_officer',
    investee: 'investee_reporter',
    lp: 'lp_viewer',
    auditor: 'auditor',
    user: 'fund_manager', // default for generic users
  };

  return mapping[siRole] ?? 'fund_manager';
}
